import type { CSSProperties, FC } from "react";
import { useState } from "react";

import { List, InfiniteLoader, AutoSizer } from "react-virtualized";
import styled from "styled-components";

import { translate } from "app/filters/i18nFilter";
import { buildAiTopicKeywordListId } from "app/modules/demand-analysis/keyword-lists";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { allTrackers } from "app/services/track/track";
import { NoKeywordResults } from "pro-features/components/AutocompleteKeywords/components/AutocompleteKeywordGroupsComponents";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import type { ITopic } from "pro-features/pages/module start page/src/keyword analysis/topics/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import AutocompleteTopicsListItem from "./components/autocomplete-topics-list-item/AutocompleteTopicsListItem";
import { ROW_HEIGHT } from "./consts";

const AUTOCOMPLETE_BOX_RESULTS_HEIGHT = 400;
const ROW_WIDTH = 604;

export const AutocompleteTopicsWrapper = styled.div`
    padding: 8px 0px;
    box-sizing: border-box;
    max-height: 400px;
    width: 100%;
    overflow: hidden;
`;

interface IAutocompleteTopics {
    topics: ITopic[];
    hasQuery: boolean;
    query: string;
    width?: number;
    boxHeight?: number;
    countryId?: number;
    selectedItemId: number;
    onClick?: (topic: ITopic) => void;
}

const onTopicItemClick = (topic: ITopic, countryId: number) => {
    const { Name } = topic;
    allTrackers.dispatchTrackEvent("Select keyword list", "click", "Auto complete", {
        target_KW_list_type: "AiTopic",
        target_KW_list_name: Name,
    });

    const keywordListId = buildAiTopicKeywordListId(Name);

    void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
        ...getDefaultParamsForState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE),
        id: [keywordListId],
        country: countryId,
    });
};

const AutocompleteTopics: FC<IAutocompleteTopics> = ({
    topics,
    hasQuery,
    query,
    selectedItemId,
    width = ROW_WIDTH,
    boxHeight = AUTOCOMPLETE_BOX_RESULTS_HEIGHT,
    countryId = WORLDWIDE_COUNTRY_ID,
    onClick,
}) => {
    const [isListHovered, setIsListHovered] = useState(false);
    const handleTopicClick = onClick ?? ((topic: ITopic) => onTopicItemClick(topic, countryId));
    const rowRenderer = ({ key, index, style }: { key: string; index: number; style: CSSProperties }) => {
        const item = topics[index];
        return (
            <div
                key={key}
                style={style}
                onMouseEnter={() => setIsListHovered(true)}
                onMouseLeave={() => setIsListHovered(false)}
            >
                <AutocompleteTopicsListItem
                    topic={item}
                    isActive={!isListHovered ? selectedItemId === index : null}
                    onClick={handleTopicClick}
                    query={query}
                />
            </div>
        );
    };

    const isRowLoaded = ({ index }) => {
        return !!topics[index];
    };

    const loadMoreRows = () => {
        // This function can be used to load more rows if needed
        return Promise.resolve();
    };

    const getScrollToIndex = (): number => {
        if (!hasQuery) {
            return 0;
        }
        const index = topics.findIndex((topic) => topic.Name === query);
        return index !== -1 ? index : 0;
    };

    if (!topics.length) {
        return <NoKeywordResults title={translate("autocomplete.topics.noTopicsFound")} />;
    }

    // Calculate the dynamic height
    const listHeight = Math.min(topics.length * ROW_HEIGHT, boxHeight);

    return (
        <AutocompleteTopicsWrapper data-automation="AutocompleteTopics">
            <InfiniteLoader
                isRowLoaded={isRowLoaded}
                loadMoreRows={loadMoreRows}
                rowCount={topics.length}
            >
                {({ onRowsRendered, registerChild }) => (
                    <AutoSizer disableHeight>
                        {({ width }) => (
                            <List
                                height={listHeight}
                                rowCount={topics.length}
                                rowHeight={ROW_HEIGHT}
                                rowRenderer={rowRenderer}
                                width={width}
                                onRowsRendered={onRowsRendered}
                                ref={registerChild}
                                scrollToIndex={getScrollToIndex()}
                            />
                        )}
                    </AutoSizer>
                )}
            </InfiniteLoader>
        </AutocompleteTopicsWrapper>
    );
};

export default AutocompleteTopics;
